import type { AnnotationLabelT } from '../../../common/types/types';
import { ANNOTATION_TYPE } from '../../labelling/constants';
import type { AnnotationT } from '../../labelling/types';
import type { TrackedObjectT, TrackObjectsOutputT, TrackObjectsRequestT } from '../../types';
import type { MlTrackingJobT } from '../atoms/ml-tracking-atoms';

// Minimal logger surface — accepts the app's Logger (whose `error` also takes context).
type TrackingLoggerT = {
  error: (message: string) => void;
};

/**
 * Maps a single tracked object's per-frame boxes to frame-indexed RECTANGLE annotations.
 * Frames without a valid bbox are skipped (the tracker may drop/occlude frames). Mirrors
 * the shape produced by `mapBoundingBoxTrackingDataToFrameAnnotations` so the existing
 * `UPDATE_TRACKING_ANNOTATION_FRAMES` apply path can consume it unchanged.
 */
export const mapTrackedObjectToFrameAnnotations = (trackedObject: TrackedObjectT, annotationLabel: AnnotationLabelT, sourceAnnotation?: AnnotationT): Record<number, AnnotationT> => {
  return trackedObject.frames.reduce<Record<number, AnnotationT>>((acc, {
    frame,
    bbox
  }) => {
    if (!bbox) {
      return acc;
    }
    acc[frame] = {
      annotationType: ANNOTATION_TYPE.RECTANGLE,
      data: bbox,
      annotationLabel,
      ...(sourceAnnotation?.metadata && {
        metadata: sourceAnnotation.metadata
      })
    };
    return acc;
  }, {});
};
type TrackAcrossKeyframesArgsT = {
  job: MlTrackingJobT;
  trackObjects: (input: TrackObjectsRequestT) => Promise<TrackObjectsOutputT | Error>;
  annotationLabel: AnnotationLabelT;
  // Carries the source annotation's metadata (e.g. mlData) onto the tracked frames.
  sourceAnnotation?: AnnotationT;
  applyTrackedFrames: (annotationId: string, startFrame: number, endFrame: number, frameAnnotations: Record<number, AnnotationT>) => void;
  logger?: TrackingLoggerT;
};

/**
 * Runs one background ML-tracking job: issues a single batch call for the object's
 * adjusted keyframes and applies the returned track by frame index.
 */
export const trackAcrossKeyframes = async ({
  job,
  trackObjects,
  annotationLabel,
  sourceAnnotation,
  applyTrackedFrames,
  logger
}: TrackAcrossKeyframesArgsT): Promise<void> => {
  const response = await trackObjects({
    keyFrames: job.keyFrames,
    startFrame: job.startFrame,
    endFrame: job.endFrame
  });
  if (response instanceof Error) {
    logger?.error(`ML tracking failed for ${job.displayName}: ${response.message}`);
    throw response;
  }
  if (!response.success) {
    const message = response.error ?? response.data?.error ?? 'unknown error';
    logger?.error(`ML tracking failed for ${job.displayName}: ${message}`);
    throw new Error(message);
  }
  const trackedObject = response.data.objects[0];
  if (!trackedObject) {
    const message = `ML tracking returned no tracked object for ${job.displayName}`;
    logger?.error(message);
    throw new Error(message);
  }
  const frameAnnotations = mapTrackedObjectToFrameAnnotations(trackedObject, annotationLabel, sourceAnnotation);
  applyTrackedFrames(job.annotationId, job.startFrame, job.endFrame, frameAnnotations);
};